import { useEffect, useRef } from "react";

import { notifyError } from "common/utils/app-monitoring/notify";
import { useEnhancementPolling } from "@design-lab/shared/hooks/useEnhancementPolling";
import { trackDesignLabEnhancementFailed } from "@design-lab/shared/lib/analytics";
import { useNotification } from "@design-lab/shared/ui/notification";

import type { Artwork } from "@design-lab/shared/types/artwork";
import { useArtwork } from "./useArtwork";
import type { ArtworkMachine } from "./useArtwork";
import { useImagesWarmFastCache } from "./useImagesFastCache";

type UseArtworkWithPollingOptions = {
  artworkId?: number | undefined;
  startTime?: number | undefined;
  machine: ArtworkMachine;
  enabled?: boolean;
};

export function useArtworkWithPolling({
  artworkId,
  startTime,
  machine,
  enabled = true
}: UseArtworkWithPollingOptions) {
  const hasStartedPolling = useRef(false);
  const hasCompletedEnhancement = useRef(false);
  const { warmCache } = useImagesWarmFastCache();
  const { getArtwork } = useArtwork();
  const { error: notifyUserError } = useNotification();

  const { startPolling, stopPolling, isPolling } = useEnhancementPolling({
    artworkId: artworkId || 0,
    startTime: startTime || Date.now(),
    onEnhancementComplete: async (artwork: unknown) => {
      const parsed = artwork as Artwork;
      // An in-flight poll can resolve after the AnyCable source has already finished this
      // artwork, but this callback closed over the machine snapshot from its scheduling render.
      // Read the live snapshot so either completion source can observe the other's win.
      const live = machine.getSnapshot?.() ?? machine.state;
      if (live.matches("finished")) {
        return;
      }
      // The polling payload carries a string id while completedArtworkId is numeric, so
      // coerce before comparing or the cross-source dedup never matches.
      const parsedId = parsed.id !== undefined ? Number(parsed.id) : undefined;
      if (
        parsedId !== undefined &&
        !Number.isNaN(parsedId) &&
        live.context.completedArtworkId === parsedId
      ) {
        return;
      }
      // A poll for a prior artwork can resolve after a new upload reuses this side's machine —
      // including the brief reset window where the machine has cleared its id but not yet taken
      // the new upload's. Proceed only when the live id is exactly the polled one; any other value
      // (a different id, or none) is stale and must not drive completion into the new upload.
      if (parsedId !== undefined && live.context.artworkId !== parsedId) {
        return;
      }
      if (!hasCompletedEnhancement.current) {
        hasCompletedEnhancement.current = true;

        try {
          if (live.matches("processing.uploaded")) {
            machine.send({ type: "enhancement.started" });
          }

          machine.send({ type: "cache.warming.started" });

          const imageUrl = parsed.noBgUrl || parsed.noBgNoTrimUrl || parsed.withBgUrl;
          if (imageUrl) {
            await warmCache([imageUrl]);
          }

          // The side can be reset/reused while the old poll is warming. Re-check the live
          // artwork id before sending completion so a stale poll cannot finish a new upload.
          const latest = machine.getSnapshot?.() ?? machine.state;
          if (
            latest.matches("finished") ||
            parsedId === undefined ||
            Number.isNaN(parsedId) ||
            latest.context.artworkId !== parsedId
          ) {
            return;
          }

          machine.send({ type: "cache.warmed" });
          machine.send({ type: "enhancement.completed", artwork: parsed });
        } catch (error) {
          // The other completion source can finish this artwork during the warmCache await. If
          // it did, the redundant warm failing is moot and resetting would bounce the finished
          // artwork back to upload, so re-read live state and bail before the reset below.
          const latest = machine.getSnapshot?.() ?? machine.state;
          if (
            latest.matches("finished") ||
            parsedId === undefined ||
            Number.isNaN(parsedId) ||
            latest.context.artworkId !== parsedId ||
            latest.context.completedArtworkId === parsedId
          ) {
            return;
          }
          console.error(`[ArtworkPolling] Error during enhancement completion:`, error);
          notifyError("Artwork polling error during enhancement completion", {
            params: {
              error: error instanceof Error ? error.message : String(error),
              error_object: error instanceof Error ? error.toString() : String(error),
              stack_trace: error instanceof Error ? error.stack : null,
              hook: "useArtworkWithPolling",
              artworkId
            }
          });

          // Track analytics for the cache warming failure, consistent with
          // onEnhancementFailed so both user-visible failure paths are measured.
          trackDesignLabEnhancementFailed({
            artworkId: String(artworkId),
            errorMessage: error instanceof Error ? error.message : String(error)
          });

          // Reset so the user can retry — without this the machine stays
          // stuck in processing.warming with no recovery path.
          hasCompletedEnhancement.current = false;
          machine.send({
            type: "reset",
            targetState: "initial",
            context: {
              artworkId,
              visitedStates: [],
              cacheWarmingStatus: "none"
            }
          });

          notifyUserError({
            title: "Enhancement Failed",
            description: "Something went wrong while loading your enhanced image. Please try again."
          });
        }
      }
    },
    onEnhancementFailed: async (errorMessage: string) => {
      // Track the failure analytics
      trackDesignLabEnhancementFailed({
        artworkId: String(artworkId),
        errorMessage
      });

      // Reset the machine to initial state (same as upload failure)
      machine.send({
        type: "reset",
        targetState: "initial",
        context: {
          artworkId,
          visitedStates: [],
          cacheWarmingStatus: "none"
        }
      });

      notifyUserError({
        title: "Enhancement Failed",
        description: errorMessage
      });
    },
    enabled: enabled && !!artworkId && !!startTime,
    isArtworkPresent: !!artworkId
  });

  useEffect(() => {
    hasStartedPolling.current = false;
    hasCompletedEnhancement.current = false;
  }, [artworkId]);

  useEffect(() => {
    if (!artworkId || machine.state.matches("finished") || machine.state.matches("initial")) {
      return;
    }

    if (
      enabled &&
      startTime &&
      !hasStartedPolling.current &&
      (machine.state.matches("processing.uploaded") ||
        machine.state.matches("processing.enhancing"))
    ) {
      hasStartedPolling.current = true;
      startPolling();
    }
  }, [enabled, artworkId, startTime, machine.state, startPolling]);

  useEffect(() => {
    if (!artworkId) return;

    if (machine.state.matches("finished") && isPolling) {
      stopPolling();
    }
  }, [machine.state, isPolling, stopPolling, artworkId, getArtwork]);

  useEffect(() => {
    if (!artworkId) return;

    if (machine.state.matches("initial")) {
      hasStartedPolling.current = false;
      hasCompletedEnhancement.current = false;
      stopPolling();
    }
  }, [machine.state, stopPolling, artworkId]);

  return {
    isPolling,
    stopPolling
  };
}
